/**
 * useAppointments — TanStack Query data layer for the Agenda (calendar) module.
 *
 * A calendar unifies THREE sources of events:
 *   - standalone appointments (this module's `appointments` table): visits,
 *     on-site quotes, meetings, reminders. Flat rows (no children).
 *   - scheduled service orders (`service_orders.scheduled_date != null`), which
 *     already exist — nothing is written to them here; they only render.
 *   - finance entries (`finance_entries_with_status.due_date`), read through the
 *     existing offline-first finance hook. Members receive no finance rows.
 *
 * Timezone note (America/Sao_Paulo): appointments store `starts_at` as a real
 * instant (local wall-clock → toISOString), while service orders and finance
 * entries carry date-only values. `buildCalendarEvents` normalizes the latter
 * to LOCAL dates. Never group `scheduled_date` or `due_date` with
 * `new Date(value)` directly — that shifts it to the previous day under UTC-3.
 *
 * Offline mutations are delegated to useOfflineSync, which enqueues the op and
 * updates the local cache. An `offline_` id must never reach Postgres — see
 * [[orcamigo-offline-sync-uuid-bug]].
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useServiceOrders } from '@/hooks/useServiceOrders';
import { useFinanceEntriesRaw } from '@/hooks/useFinance';
import { useRole } from '@/hooks/useRole';
import { cacheAppointments, getCachedAppointments, getCachedClients } from '@/lib/offlineDataCache';
import type { Database } from '@/integrations/supabase/types';

type AppointmentInsert = Database['public']['Tables']['appointments']['Insert'];
import { buildCalendarEvents } from '@/lib/calendarEvents';
import type { AppointmentStatus } from '@/types/database';
import { toast } from 'sonner';

const APPOINTMENTS_KEY = 'appointments';

// Stable empty reference so `useAppointments().appointments` keeps identity
// while the query is disabled/loading — otherwise the reminder-reconcile effect
// in AgendaPage would re-run on every render.
const EMPTY_APPOINTMENTS: any[] = [];

export const APPOINTMENT_STATUSES: { value: AppointmentStatus; label: string }[] = [
  { value: 'scheduled', label: 'Agendado' },
  { value: 'done', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
];

export const REMINDER_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'Sem lembrete' },
  { value: 15, label: '15 minutos antes' },
  { value: 30, label: '30 minutos antes' },
  { value: 60, label: '1 hora antes' },
  { value: 1440, label: '1 dia antes' },
];

export interface AppointmentInput {
  client_id?: string | null;
  client_name?: string | null;
  service_order_id?: string | null;
  quote_id?: string | null;
  title?: string | null;
  description?: string | null;
  location?: string | null;
  starts_at: string;
  ends_at?: string | null;
  all_day?: boolean;
  status?: AppointmentStatus;
  reminder_minutes?: number | null;
}

// ===== Read =====

/**
 * Base query: every appointment for the active company. Cached locally so the
 * agenda keeps working offline. Date-range filtering is done client-side (in the
 * calendar UI) to preserve the offline snapshot.
 */
export function useAppointments() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [APPOINTMENTS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // Mesma razão do `useServiceOrders`: um compromisso criado em outro
    // dispositivo tem que aparecer na agenda sem esperar o cache envelhecer.
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedAppointments(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<any[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('company_id', companyId)
        .order('starts_at', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as any[];
      cacheAppointments(user.id, rows);
      return rows;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY, companyId] });
  }, [queryClient, companyId]);

  const syncFromCache = useCallback(() => {
    if (!user?.id) return;
    queryClient.setQueryData([APPOINTMENTS_KEY, companyId], getCachedAppointments(user.id) ?? []);
  }, [queryClient, user?.id, companyId]);

  return { ...query, appointments: query.data ?? EMPTY_APPOINTMENTS, invalidate, syncFromCache };
}

const CLIENT_BIRTHDAYS_KEY = 'client-birthdays';

/**
 * Clientes com aniversário, para a Agenda.
 *
 * Query PRÓPRIA e enxuta (4 colunas) em vez de reusar a lista de clientes: a
 * Agenda só precisa de quem TEM `birth_date`, e o filtro no servidor evita
 * trazer o cadastro inteiro para desenhar um punhado de eventos.
 *
 * ⚠️ NÃO reuse um hook de dados sem ler o `queryFn` inteiro (#37): o de
 * categorias do financeiro SEMEIA linhas na primeira leitura, e um hook de
 * clientes com efeito parecido faria a Agenda escrever no cadastro.
 */
export function useClientBirthdays() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const query = useQuery({
    queryKey: [CLIENT_BIRTHDAYS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem):
    // sem o carimbo o cache semeado conta como FRESCO e a primeira busca nunca
    // sai — o aniversário cadastrado em outro aparelho ficaria invisível (#158).
    initialDataUpdatedAt: 0,
    initialData: () =>
      user?.id
        ? (getCachedClients(user.id) ?? undefined)?.filter((c: any) => c?.birth_date)
        : undefined,
    queryFn: async (): Promise<any[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, birth_date, photo_url')
        .eq('company_id', companyId)
        .not('birth_date', 'is', null);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  return { ...query, clients: query.data ?? EMPTY_APPOINTMENTS };
}

// ===== Calendário =====

/**
 * Unified calendar events: appointments + scheduled service orders + finance
 * due dates + client birthdays. Reads the existing offline-first queries and
 * aggregates client-side.
 */
export function useCalendarEvents(visibleYear?: number) {
  const appointmentsQuery = useAppointments();
  const ordersQuery = useServiceOrders();
  const financeQuery = useFinanceEntriesRaw();
  const { canSeeFinance } = useRole();
  const financeEntries = canSeeFinance ? financeQuery.entries : EMPTY_APPOINTMENTS;
  const invalidateAppointments = appointmentsQuery.invalidate;
  const invalidateOrders = ordersQuery.invalidate;
  const invalidateFinance = financeQuery.invalidate;

  const birthdaysQuery = useClientBirthdays();
  const birthdayClients = birthdaysQuery.clients;

  /*
   * Os anos em que projetar os aniversários (o único evento RECORRENTE).
   *
   * Inclui o ano VISÍVEL ±1 porque a grade de um mês transborda para o ano
   * vizinho (a de dezembro mostra o começo de janeiro), e o ano CORRENTE porque
   * qualquer lista de "próximos" fala de hoje, não do mês que está na tela.
   */
  const anos = useMemo(() => {
    const base = visibleYear ?? new Date().getFullYear();
    return [...new Set([base - 1, base, base + 1, new Date().getFullYear()])];
  }, [visibleYear]);

  const events = useMemo(
    () =>
      buildCalendarEvents(
        appointmentsQuery.appointments,
        ordersQuery.orders,
        financeEntries,
        birthdayClients,
        anos,
      ),
    [appointmentsQuery.appointments, ordersQuery.orders, financeEntries, birthdayClients, anos],
  );
  const invalidate = useCallback(() => {
    invalidateAppointments();
    invalidateOrders();
    invalidateFinance();
  }, [invalidateAppointments, invalidateOrders, invalidateFinance]);

  return {
    events,
    appointments: appointmentsQuery.appointments,
    orders: ordersQuery.orders,
    financeEntries,
    isLoading:
      appointmentsQuery.isLoading ||
      ordersQuery.isLoading ||
      (canSeeFinance && financeQuery.isLoading),
    isFetching:
      appointmentsQuery.isFetching ||
      ordersQuery.isFetching ||
      (canSeeFinance && financeQuery.isFetching),
    invalidate,
  };
}

/** A single appointment by id, from the cached list (works offline). */
export function useAppointment(id: string | undefined) {
  const raw = useAppointments();
  const appointment = useMemo(
    () => (id ? raw.appointments.find((a: any) => a.id === id) ?? null : null),
    [raw.appointments, id],
  );
  return { ...raw, appointment };
}

// ===== Mutations =====

/** Normalize an input into the scalar column set stored on `appointments`. */
function toAppointmentFields(input: Partial<AppointmentInput>): Record<string, any> {
  const fields: Record<string, any> = {};
  const set = (key: string, value: any) => {
    if (value !== undefined) fields[key] = value;
  };
  set('client_id', input.client_id ?? null);
  set('client_name', input.client_name ?? null);
  set('service_order_id', input.service_order_id ?? null);
  set('quote_id', input.quote_id ?? null);
  set('title', input.title ?? null);
  set('description', input.description ?? null);
  set('location', input.location ?? null);
  set('starts_at', input.starts_at);
  set('ends_at', input.ends_at ?? null);
  set('all_day', input.all_day ?? false);
  set('status', input.status ?? 'scheduled');
  set('reminder_minutes', input.reminder_minutes ?? null);
  return fields;
}

export function useCreateAppointment() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { saveAppointmentOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: AppointmentInput) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      if (!input.starts_at) throw new Error('Data/hora obrigatória');

      const fields = toAppointmentFields(input);
      const online = await checkNetworkStatus();

      if (!online) {
        saveAppointmentOffline({ appointment: fields, userId: user.id, companyId });
        queryClient.setQueryData([APPOINTMENTS_KEY, companyId], getCachedAppointments(user.id) ?? []);
        return { offline: true as const };
      }

      const { data: created, error } = await supabase
        .from('appointments')
        .insert({ ...fields, user_id: user.id, company_id: companyId } as AppointmentInsert)
        .select('*')
        .single();
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('appointments-updated'));
      return { offline: false as const, id: (created as any).id as string };
    },
    onError: () => toast.error('Não foi possível salvar o compromisso.'),
  });
}

export function useUpdateAppointment() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { updateAppointmentOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; updates: Partial<AppointmentInput> }) => {
      if (!user?.id) throw new Error('Sem sessão');

      const updates = toAppointmentFields(input.updates);

      // A still-pending offline create carries an offline_ id — never send it to
      // Postgres (invalid uuid), even when online. Fold the edit into the pending
      // create instead. See [[orcamigo-offline-sync-uuid-bug]].
      const online = String(input.id).startsWith('offline_') ? false : await checkNetworkStatus();

      if (!online) {
        updateAppointmentOffline({ appointmentId: input.id, updates, userId: user.id });
        queryClient.setQueryData([APPOINTMENTS_KEY, companyId], getCachedAppointments(user.id) ?? []);
        return;
      }

      let query = supabase.from('appointments').update(updates).eq('id', input.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('appointments-updated'));
    },
    onError: () => toast.error('Não foi possível atualizar o compromisso.'),
  });
}

export function useDeleteAppointment() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { deleteAppointmentOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('Sem sessão');

      // Offline_ id (pending create not yet synced): handle locally, never hit
      // Postgres with a non-uuid. See [[orcamigo-offline-sync-uuid-bug]].
      const online = String(id).startsWith('offline_') ? false : await checkNetworkStatus();

      if (!online) {
        deleteAppointmentOffline({ appointmentId: id, userId: user.id });
        queryClient.setQueryData([APPOINTMENTS_KEY, companyId], getCachedAppointments(user.id) ?? []);
        return;
      }

      let query = supabase.from('appointments').delete().eq('id', id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [APPOINTMENTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('appointments-updated'));
    },
    onError: () => toast.error('Não foi possível excluir o compromisso.'),
  });
}
